import { useState, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { r2Upload, r2Delete } from '@/lib/r2';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import {
  Megaphone, ImageIcon, Plus, Trash2, Pencil, Upload,
  ToggleLeft, ToggleRight, Globe, MapPin, Monitor, Package, Users,
  ChevronUp, ChevronDown, Eye, Code2, Clock, LayoutList, Image,
  Copy, AlertTriangle, Sun,
} from 'lucide-react';
import type { AdAsset, AdCampaign, CampaignPlaylistItem, Targeting } from '@/types/demo';
import { PRODUCT_FAMILIES } from '@/types/demo';
import { getTargetedStationIds } from '@/lib/targeting';
import type { DemoLocation, DemoStation } from '@/types/demo';
import { sendCommand } from '@/lib/api/demoStations';
import { toast } from 'sonner';

// ── HtmlThumb — live-rendered iframe thumbnail ────────────────────────────────

function HtmlThumb({ html }: { html: string }) {
  return (
    <iframe
      srcDoc={html}
      sandbox="allow-scripts"
      className="w-full h-full border-0 pointer-events-none"
      title=""
    />
  );
}

// ── helpers ───────────────────────────────────────────────────────────────────

function campaignStatus(c: AdCampaign): { label: string; color: string } {
  if (!c.is_active) return { label: 'Paused',    color: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' };
  const now = new Date();
  if (c.starts_at && new Date(c.starts_at) > now) return { label: 'Scheduled', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' };
  if (c.ends_at   && new Date(c.ends_at)   < now) return { label: 'Ended',     color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' };
  return { label: 'Live', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' };
}

function formatSchedule(c: AdCampaign): string {
  const fmt = (s: string) => new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  if (!c.starts_at && !c.ends_at) return 'Always';
  if (!c.starts_at && c.ends_at)  return `Until ${fmt(c.ends_at)}`;
  if (c.starts_at  && !c.ends_at) return `From ${fmt(c.starts_at)}`;
  return `${fmt(c.starts_at!)} → ${fmt(c.ends_at!)}`;
}

function formatTiming(c: AdCampaign): string | null {
  if (!c.display_duration_seconds || !c.display_interval_seconds) return null;
  const dur = c.display_duration_seconds;
  const ivl = c.display_interval_seconds;
  const fmtSec = (s: number) => s >= 60 ? `${Math.round(s / 60)}m` : `${s}s`;
  return `${fmtSec(dur)} every ${fmtSec(ivl)}`;
}


function toLocalDatetimeInput(date: Date): string {
  const off = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - off).toISOString().slice(0, 16);
}

// ── Form state ────────────────────────────────────────────────────────────────

interface PlaylistFormItem {
  _key: string;
  asset_id: string;
  duration_seconds: number;
}

interface CampaignForm {
  name: string;
  mode: 'single' | 'playlist';
  asset_id: string;
  playlist_items: PlaylistFormItem[];
  targeting: Targeting;
  starts_at: string;
  ends_at: string;
  has_start: boolean;
  has_end: boolean;
  priority: number;
  is_active: boolean;
  has_display_timing: boolean;
  display_duration_seconds: number;
  display_interval_seconds: number;
  has_time_window: boolean;
  show_from: string;
  show_until: string;
}

const BLANK_FORM: CampaignForm = {
  name: '', mode: 'single', asset_id: '', playlist_items: [],
  targeting: {},
  starts_at: '', ends_at: '', has_start: false, has_end: false,
  priority: 0, is_active: true,
  has_display_timing: false, display_duration_seconds: 30, display_interval_seconds: 300,
  has_time_window: false, show_from: '09:00', show_until: '21:00',
};

// ── Replay script helpers ─────────────────────────────────────────────────────

const REPLAY_MARKER = 'aoi-replay';

function buildReplayScript(interval: number): string {
  // Clones the first .rev-scene element to restart all CSS animations from scratch.
  // Script is idempotent: only one interval runs at a time via the marker attribute.
  return `<script id="${REPLAY_MARKER}" data-interval="${interval}">`
    + `(function(){`
    + `var t=${interval};`
    + `setTimeout(function(){`
    + `setInterval(function(){`
    + `var n=document.querySelector('.rev-scene');`
    + `if(n){var c=n.cloneNode(true);n.parentNode.replaceChild(c,n);}},`
    + `t*1000);},t*1000);`
    // The backslash is deliberate: this string carries a literal </script>, and
    // escaping the slash stops it terminating an enclosing script block early if
    // this bundle is ever inlined into HTML.
    // eslint-disable-next-line no-useless-escape
    + `})();<\/script>`;
}

function injectReplay(html: string, enabled: boolean, interval: number): string {
  const cleaned = html.replace(new RegExp(`<script id="${REPLAY_MARKER}"[\\s\\S]*?<\\/script>`, 'g'), '').trim();
  return enabled ? cleaned + '\n' + buildReplayScript(Math.max(5, interval)) : cleaned;
}

function parseReplay(html: string): { enabled: boolean; interval: number } {
  const m = html.match(new RegExp(`<script id="${REPLAY_MARKER}" data-interval="(\\d+)"`));
  return m ? { enabled: true, interval: parseInt(m[1]) } : { enabled: false, interval: 30 };
}

// ── Component ─────────────────────────────────────────────────────────────────

export function AdCampaignsPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'campaigns' | 'assets'>(() => {
    const saved = localStorage.getItem('adCampaignsTab');
    return saved === 'campaigns' || saved === 'assets' ? saved : 'assets';
  });

  // ── Queries ────────────────────────────────────────────────────────────────

  const { data: campaigns = [], isLoading: camLoading } = useQuery<AdCampaign[]>({
    queryKey: ['ad-campaigns'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ad_campaigns')
        .select(`*, asset:ad_assets(*), playlist_items:campaign_playlist_items(*, asset:ad_assets(*))`)
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []).map((c: any) => ({
        ...c,
        playlist_items: (c.playlist_items ?? []).sort((a: CampaignPlaylistItem, b: CampaignPlaylistItem) => a.sort_order - b.sort_order),
      }));
    },
    refetchInterval: 30_000,
  });

  const { data: assets = [], isLoading: assetsLoading } = useQuery<AdAsset[]>({
    queryKey: ['ad-assets'],
    queryFn: async () => {
      const { data, error } = await supabase.from('ad_assets').select('*').order('created_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: locations = [] } = useQuery<DemoLocation[]>({
    queryKey: ['demo-locations'],
    queryFn: async () => { const { data } = await supabase.from('demo_locations').select('*').order('sort_order'); return data ?? []; },
  });

  const { data: stations = [] } = useQuery<(DemoStation & { location: { name: string } | null })[]>({
    queryKey: ['demo-stations-list'],
    queryFn: async () => { const { data } = await supabase.from('demo_stations').select('id, name, location_id, build_id, status, location:demo_locations(name)').order('name'); return (data ?? []) as unknown as (DemoStation & { location: { name: string } | null })[]; },
  });

  const { data: builds = [] } = useQuery<{ id: string; sku: string; description: string | null }[]>({
    queryKey: ['builds-simple'],
    queryFn: async () => { const { data } = await supabase.from('builds').select('id, sku, description').order('sku'); return data ?? []; },
  });

  // ── Campaign mutations ─────────────────────────────────────────────────────

  const upsertCampaign = useMutation({
    mutationFn: async ({ id, form }: { id?: string; form: CampaignForm }) => {
      const payload: Record<string, unknown> = {
        name:                    form.name.trim(),
        asset_id:                form.mode === 'single' ? (form.asset_id || null) : null,
        targeting:               form.targeting,
        target_type:             'all',  // legacy column — kept for backwards compat
        target_id:               null,
        starts_at:               form.has_start && form.starts_at ? new Date(form.starts_at).toISOString() : null,
        ends_at:                 form.has_end   && form.ends_at   ? new Date(form.ends_at).toISOString()   : null,
        priority:                form.priority,
        is_active:               form.is_active,
        display_duration_seconds: form.has_display_timing ? form.display_duration_seconds : null,
        display_interval_seconds: form.has_display_timing ? form.display_interval_seconds : null,
        show_from:  form.has_time_window ? form.show_from  : null,
        show_until: form.has_time_window ? form.show_until : null,
      };

      let campaignId = id;
      if (id) {
        const { error } = await supabase.from('ad_campaigns').update(payload).eq('id', id);
        if (error) throw error;
      } else {
        const { data, error } = await supabase.from('ad_campaigns').insert(payload).select('id').single();
        if (error) throw error;
        campaignId = data.id;
      }

      // Replace playlist items (delete then insert)
      await supabase.from('campaign_playlist_items').delete().eq('campaign_id', campaignId!);
      if (form.mode === 'playlist' && form.playlist_items.length > 0) {
        const { error } = await supabase.from('campaign_playlist_items').insert(
          form.playlist_items.map((item, i) => ({
            campaign_id:      campaignId,
            asset_id:         item.asset_id,
            sort_order:       i,
            duration_seconds: item.duration_seconds,
          }))
        );
        if (error) throw error;
      }
    },
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: ['ad-campaigns'] });
      setDialogOpen(false);
      toast.success(editingId ? 'Campaign updated' : 'Campaign created');
      // Push refresh to all active stations so changes take effect immediately
      const { data: stationList } = await supabase.from('demo_stations').select('id').neq('status', 'retiring');
      await Promise.allSettled((stationList ?? []).map((s: { id: string }) => sendCommand(s.id, 'refresh_ad')));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggleCampaign = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase.from('ad_campaigns').update({ is_active }).eq('id', id);
      if (error) throw error;
      const { data: stationList } = await supabase.from('demo_stations').select('id').neq('status', 'retiring');
      await Promise.allSettled((stationList ?? []).map((s: { id: string }) => sendCommand(s.id, 'refresh_ad')));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ad-campaigns'] }),
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteCampaign = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('ad_campaigns').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ad-campaigns'] }); toast.success('Campaign deleted'); },
    onError: (e: Error) => toast.error(e.message),
  });

  // ── Asset mutations ────────────────────────────────────────────────────────

  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadingAsset, setUploadingAsset] = useState(false);
  const [assetName, setAssetName]           = useState('');
  const [pendingFile, setPendingFile]       = useState<File | null>(null);
  const [htmlAssetName, setHtmlAssetName]   = useState('');
  const [htmlContent, setHtmlContent]       = useState('');
  const [savingHtml, setSavingHtml]         = useState(false);
  const [previewAsset, setPreviewAsset]     = useState<AdAsset | null>(null);
  const [editingHtmlAsset, setEditingHtmlAsset] = useState<AdAsset | null>(null);
  const [editHtmlName, setEditHtmlName]         = useState('');
  const [editHtmlContent, setEditHtmlContent]   = useState('');
  const [savingEditHtml, setSavingEditHtml]     = useState(false);
  const [previewHtml, setPreviewHtml]           = useState<string | null>(null);

  // Replay config — create form
  const [htmlReplayEnabled,  setHtmlReplayEnabled]  = useState(false);
  const [htmlReplayInterval, setHtmlReplayInterval] = useState(30);
  // Replay config — edit form
  const [editReplayEnabled,  setEditReplayEnabled]  = useState(false);
  const [editReplayInterval, setEditReplayInterval] = useState(30);

  function openEditHtmlDialog(asset: AdAsset) {
    const { enabled, interval } = parseReplay(asset.html ?? '');
    setEditingHtmlAsset(asset);
    setEditHtmlName(asset.name);
    setEditHtmlContent(asset.html ?? '');
    setEditReplayEnabled(enabled);
    setEditReplayInterval(interval);
  }

  async function handleUpdateHtmlAsset() {
    if (!editingHtmlAsset || !editHtmlName.trim() || !editHtmlContent.trim()) return;
    setSavingEditHtml(true);
    try {
      const finalHtml = injectReplay(editHtmlContent.trim(), editReplayEnabled, editReplayInterval);
      const { error } = await supabase.from('ad_assets')
        .update({ name: editHtmlName.trim(), html: finalHtml })
        .eq('id', editingHtmlAsset.id);
      if (error) throw error;
      qc.invalidateQueries({ queryKey: ['ad-assets'] });
      setEditingHtmlAsset(null);
      toast.success('HTML asset updated');
      // Push refresh to all active stations so the updated content shows immediately
      const { data: stationList } = await supabase.from('demo_stations').select('id').neq('status', 'retiring');
      await Promise.allSettled((stationList ?? []).map((s: { id: string }) => sendCommand(s.id, 'refresh_ad')));

    } catch (e: any) { toast.error(e.message); }
    finally { setSavingEditHtml(false); }
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingFile(file);
    setAssetName(file.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' '));
  }

  async function handleUploadAsset() {
    if (!pendingFile || !assetName.trim()) return;
    setUploadingAsset(true);
    try {
      const ext  = pendingFile.name.split('.').pop();
      const path = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const publicUrl = await r2Upload(`ad-assets/${path}`, pendingFile, pendingFile.type);
      const { error: dbErr } = await supabase.from('ad_assets').insert({ name: assetName.trim(), type: 'image', image_url: publicUrl, storage_path: path });
      if (dbErr) throw dbErr;
      qc.invalidateQueries({ queryKey: ['ad-assets'] });
      setPendingFile(null); setAssetName('');
      if (fileRef.current) fileRef.current.value = '';
      toast.success('Asset uploaded');
    } catch (e: any) { toast.error(e.message); }
    finally { setUploadingAsset(false); }
  }

  async function handleSaveHtmlAsset() {
    if (!htmlAssetName.trim() || !htmlContent.trim()) return;
    setSavingHtml(true);
    try {
      const finalHtml = injectReplay(htmlContent.trim(), htmlReplayEnabled, htmlReplayInterval);
      const { error } = await supabase.from('ad_assets').insert({ name: htmlAssetName.trim(), type: 'html', html: finalHtml });
      if (error) throw error;
      qc.invalidateQueries({ queryKey: ['ad-assets'] });
      setHtmlAssetName(''); setHtmlContent(''); setHtmlReplayEnabled(false); setHtmlReplayInterval(30);
      toast.success('HTML asset saved');
    } catch (e: any) { toast.error(e.message); }
    finally { setSavingHtml(false); }
  }

  const deleteAsset = useMutation({
    mutationFn: async (asset: AdAsset) => {
      if (asset.storage_path) await r2Delete(`ad-assets/${asset.storage_path}`);
      const { error } = await supabase.from('ad_assets').delete().eq('id', asset.id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ad-assets'] }); toast.success('Asset deleted'); },
    onError: (e: Error) => toast.error(e.message),
  });

  // ── Dialog + form state ────────────────────────────────────────────────────

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId,  setEditingId]  = useState<string | null>(null);
  const [form, setForm]             = useState<CampaignForm>(BLANK_FORM);
  const [addSlideId, setAddSlideId] = useState('');

  function openCreate() { setEditingId(null); setForm(BLANK_FORM); setAddSlideId(''); setDialogOpen(true); }

  function openEdit(c: AdCampaign) {
    setEditingId(c.id);
    const hasPlaylist = (c.playlist_items?.length ?? 0) > 0;
    setForm({
      name:        c.name,
      mode:        hasPlaylist ? 'playlist' : 'single',
      asset_id:    c.asset_id ?? '',
      playlist_items: (c.playlist_items ?? []).map(pi => ({
        _key: pi.id, asset_id: pi.asset_id, duration_seconds: pi.duration_seconds,
      })),
      targeting:   c.targeting ?? {},
      starts_at:   c.starts_at ? toLocalDatetimeInput(new Date(c.starts_at)) : '',
      ends_at:     c.ends_at   ? toLocalDatetimeInput(new Date(c.ends_at))   : '',
      has_start:   !!c.starts_at,
      has_end:     !!c.ends_at,
      priority:    c.priority,
      is_active:   c.is_active,
      has_display_timing:      !!(c.display_duration_seconds && c.display_interval_seconds),
      display_duration_seconds: c.display_duration_seconds ?? 30,
      display_interval_seconds: c.display_interval_seconds ?? 300,
      has_time_window: !!(c.show_from && c.show_until),
      show_from:  c.show_from  ?? '09:00',
      show_until: c.show_until ?? '21:00',
    });
    setAddSlideId('');
    setDialogOpen(true);
  }

  function openDuplicate(c: AdCampaign) {
    setEditingId(null);
    const hasPlaylist = (c.playlist_items?.length ?? 0) > 0;
    setForm({
      name:        `Copy of ${c.name}`,
      mode:        hasPlaylist ? 'playlist' : 'single',
      asset_id:    c.asset_id ?? '',
      playlist_items: (c.playlist_items ?? []).map((pi, i) => ({
        _key: `dup-${i}-${Date.now()}`, asset_id: pi.asset_id, duration_seconds: pi.duration_seconds,
      })),
      targeting:   c.targeting ?? {},
      starts_at:   c.starts_at ? toLocalDatetimeInput(new Date(c.starts_at)) : '',
      ends_at:     c.ends_at   ? toLocalDatetimeInput(new Date(c.ends_at))   : '',
      has_start:   !!c.starts_at,
      has_end:     !!c.ends_at,
      priority:    c.priority,
      is_active:   false, // start paused
      has_display_timing:      !!(c.display_duration_seconds && c.display_interval_seconds),
      display_duration_seconds: c.display_duration_seconds ?? 30,
      display_interval_seconds: c.display_interval_seconds ?? 300,
      has_time_window: !!(c.show_from && c.show_until),
      show_from:  c.show_from  ?? '09:00',
      show_until: c.show_until ?? '21:00',
    });
    setAddSlideId('');
    setDialogOpen(true);
  }

  // Playlist helpers
  function addSlide() {
    if (!addSlideId) return;
    setForm(f => ({
      ...f,
      playlist_items: [...f.playlist_items, { _key: `${Date.now()}`, asset_id: addSlideId, duration_seconds: 10 }],
    }));
    setAddSlideId('');
  }

  function removeSlide(i: number) {
    setForm(f => ({ ...f, playlist_items: f.playlist_items.filter((_, idx) => idx !== i) }));
  }

  function moveSlide(i: number, dir: -1 | 1) {
    setForm(f => {
      const items = [...f.playlist_items];
      const j = i + dir;
      if (j < 0 || j >= items.length) return f;
      [items[i], items[j]] = [items[j], items[i]];
      return { ...f, playlist_items: items };
    });
  }

  function updateSlideDuration(i: number, val: number) {
    setForm(f => {
      const items = [...f.playlist_items];
      items[i] = { ...items[i], duration_seconds: Math.max(1, val) };
      return { ...f, playlist_items: items };
    });
  }

  function stationCount(c: AdCampaign): number {
    return getTargetedStationIds(c.targeting, stations, builds).size;
  }

  function targetLabel(c: AdCampaign) {
    const t = c.targeting ?? {};
    const parts: string[] = [];
    if (t.locations?.length) parts.push(t.locations.map(id => locations.find(l => l.id === id)?.name ?? '?').join(', '));
    if (t.families?.length) parts.push(t.families.map(code => PRODUCT_FAMILIES[code] ?? code).join(', '));
    if (t.builds?.length) parts.push(t.builds.map(id => builds.find(b => b.id === id)?.sku ?? '?').join(', '));
    if (t.stations?.length) parts.push(t.stations.map(id => stations.find(s => s.id === id)?.name ?? '?').join(', '));
    return parts.length > 0 ? parts.join(' + ') : 'All stations';
  }

  // ── Conflict detection ─────────────────────────────────────────────────────

  const conflicts = useMemo(() => {
    const formStations = getTargetedStationIds(form.targeting, stations, builds);
    if (formStations.size === 0) return [];
    return campaigns
      .filter(c => c.is_active && c.id !== editingId)
      .flatMap(c => {
        const cStations = getTargetedStationIds(c.targeting, stations, builds);
        const overlap = [...formStations].filter(id => cStations.has(id));
        return overlap.length > 0 ? [{ campaign: c, count: overlap.length }] : [];
      });
  }, [form.targeting, form.priority, campaigns, editingId, stations, builds]);

  const canSave = form.name.trim() &&
    (form.mode === 'single' ? !!form.asset_id : form.playlist_items.length > 0) &&
    (!form.has_display_timing || form.display_interval_seconds > form.display_duration_seconds);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-6xl mx-auto space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Megaphone className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-xl font-semibold">Ad Campaigns</h1>
            <p className="text-sm text-muted-foreground">Push image or HTML ads to demo stations by shop, build, or individual station</p>
          </div>
        </div>
        {tab === 'campaigns' && (
          <Button onClick={openCreate} className="gap-2" size="sm">
            <Plus className="h-4 w-4" /> New Campaign
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {(['assets', 'campaigns'] as const).map(t => (
          <button
            key={t}
            onClick={() => { setTab(t); localStorage.setItem('adCampaignsTab', t); }}
            className={cn(
              'px-4 py-2 text-sm font-medium capitalize transition-colors border-b-2 -mb-px',
              tab === t ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            {t === 'campaigns' ? `Campaigns (${campaigns.length})` : `Assets (${assets.length})`}
          </button>
        ))}
      </div>

      {/* ── Campaigns tab ─────────────────────────────────────────────────────── */}
      {tab === 'campaigns' && (
        <div className="space-y-3">
          {camLoading && [1,2,3].map(i => <div key={i} className="h-24 rounded-lg bg-gray-100 dark:bg-gray-800 animate-pulse" />)}

          {!camLoading && campaigns.length === 0 && (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground">
                <Megaphone className="h-10 w-10 opacity-30" />
                <p className="text-sm">No campaigns yet — upload an asset and create your first campaign.</p>
                <Button size="sm" variant="outline" onClick={openCreate}>New Campaign</Button>
              </CardContent>
            </Card>
          )}

          {campaigns.map(c => {
            const st       = campaignStatus(c);
            const ct = c.targeting ?? {};
            const hasT = !!(ct.locations?.length || ct.families?.length || ct.builds?.length || ct.stations?.length);
            const TIcon = hasT ? (ct.stations?.length ? Monitor : ct.locations?.length ? MapPin : ct.families?.length ? Users : Package) : Globe;
            const timing   = formatTiming(c);
            const isPlaylist = (c.playlist_items?.length ?? 0) > 0;
            const thumbAsset = isPlaylist ? c.playlist_items?.[0]?.asset : c.asset;
            const thumbUrl   = thumbAsset?.type === 'image' ? thumbAsset?.image_url : null;
            const thumbIsHtml = thumbAsset?.type === 'html';
            const count = stationCount(c);
            const isExpiringSoon = c.is_active && c.ends_at &&
              new Date(c.ends_at) > new Date() &&
              new Date(c.ends_at).getTime() - Date.now() < 7 * 24 * 60 * 60 * 1000;

            return (
              <Card key={c.id} className="overflow-hidden">
                <CardContent className="p-0">
                  <div className="flex items-stretch">
                    {/* Thumbnail */}
                    <div className="w-24 h-24 flex-shrink-0 bg-gray-100 dark:bg-gray-800 flex items-center justify-center overflow-hidden relative">
                      {thumbUrl ? (
                        <img src={thumbUrl} alt="" className="w-full h-full object-cover" />
                      ) : thumbIsHtml && thumbAsset?.html ? (
                        <HtmlThumb html={thumbAsset.html} />
                      ) : (
                        <ImageIcon className="h-6 w-6 text-gray-300" />
                      )}
                      {isPlaylist && (
                        <span className="absolute bottom-1 right-1 bg-black/60 text-white text-[9px] px-1.5 py-0.5 rounded font-medium">
                          {c.playlist_items!.length} slides
                        </span>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 px-4 py-3 min-w-0">
                      <div className="flex items-start gap-2 flex-wrap">
                        <span className="font-medium text-sm truncate">{c.name}</span>
                        <span className={cn('px-2 py-0.5 rounded-full text-[11px] font-medium', st.color)}>{st.label}</span>
                        {c.priority > 0 && (
                          <span className="px-2 py-0.5 rounded-full text-[11px] bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                            P{c.priority}
                          </span>
                        )}
                        {isPlaylist && (
                          <span className="px-2 py-0.5 rounded-full text-[11px] bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 flex items-center gap-1">
                            <LayoutList className="h-2.5 w-2.5" /> Playlist
                          </span>
                        )}
                        {timing && (
                          <span className="px-2 py-0.5 rounded-full text-[11px] bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 flex items-center gap-1">
                            <Clock className="h-2.5 w-2.5" /> {timing}
                          </span>
                        )}
                        {c.show_from && c.show_until && (
                          <span className="px-2 py-0.5 rounded-full text-[11px] bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400 flex items-center gap-1">
                            <Sun className="h-2.5 w-2.5" /> {c.show_from}–{c.show_until}
                          </span>
                        )}
                        {isExpiringSoon && (
                          <span className="px-2 py-0.5 rounded-full text-[11px] bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 flex items-center gap-1">
                            <AlertTriangle className="h-2.5 w-2.5" /> Expires soon
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-3 text-[12px] text-muted-foreground">
                        <span className="flex items-center gap-1"><TIcon className="h-3 w-3" /> {targetLabel(c)}</span>
                        <span className="flex items-center gap-1"><Monitor className="h-3 w-3" /> {count} station{count !== 1 ? 's' : ''}</span>
                        <span>{formatSchedule(c)}</span>
                        {!isPlaylist && c.asset && <span className="text-gray-400">{c.asset.name}</span>}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 pr-3">
                      <button
                        onClick={() => toggleCampaign.mutate({ id: c.id, is_active: !c.is_active })}
                        className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                        title={c.is_active ? 'Pause' : 'Activate'}
                      >
                        {c.is_active
                          ? <ToggleRight className="h-5 w-5 text-green-600" />
                          : <ToggleLeft  className="h-5 w-5 text-gray-400" />}
                      </button>
                      <button onClick={() => openEdit(c)} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" title="Edit">
                        <Pencil className="h-4 w-4 text-gray-500" />
                      </button>
                      <button onClick={() => openDuplicate(c)} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" title="Duplicate">
                        <Copy className="h-4 w-4 text-gray-500" />
                      </button>
                      <button
                        onClick={() => { if (confirm(`Delete "${c.name}"?`)) deleteCampaign.mutate(c.id); }}
                        className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* ── Assets tab ────────────────────────────────────────────────────────── */}
      {tab === 'assets' && (
        <div className="space-y-6">

          {/* Upload image */}
          <Card>
            <CardHeader className="pb-2">
              <h2 className="text-sm font-semibold flex items-center gap-2"><Image className="h-4 w-4" /> Upload Image</h2>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-end gap-3 flex-wrap">
                <div className="space-y-1 flex-1 min-w-[200px]">
                  <Label className="text-xs text-muted-foreground">Image file (JPEG / PNG / GIF / WebP, max 10 MB)</Label>
                  <input
                    ref={fileRef} type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleFileChange}
                    className="block w-full text-sm text-muted-foreground file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-medium file:bg-primary file:text-primary-foreground hover:file:opacity-90 cursor-pointer"
                  />
                </div>
                <div className="space-y-1 flex-1 min-w-[180px]">
                  <Label className="text-xs text-muted-foreground">Asset name</Label>
                  <Input placeholder="e.g. Summer Sale Banner" value={assetName} onChange={e => setAssetName(e.target.value)} className="h-8 text-sm" />
                </div>
                <Button size="sm" onClick={handleUploadAsset} disabled={!pendingFile || !assetName.trim() || uploadingAsset} className="gap-2">
                  {uploadingAsset ? 'Uploading…' : <><Upload className="h-3 w-3" /> Upload</>}
                </Button>
              </div>
              {pendingFile && <p className="text-xs text-muted-foreground">Selected: {pendingFile.name} ({(pendingFile.size / 1024).toFixed(0)} KB)</p>}
            </CardContent>
          </Card>

          {/* Create HTML ad */}
          <Card>
            <CardHeader className="pb-2">
              <h2 className="text-sm font-semibold flex items-center gap-2"><Code2 className="h-4 w-4" /> Create HTML Ad</h2>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-3 flex-wrap">
                <div className="space-y-1 flex-1 min-w-[200px]">
                  <Label className="text-xs text-muted-foreground">Asset name</Label>
                  <Input placeholder="e.g. Promo HTML Banner" value={htmlAssetName} onChange={e => setHtmlAssetName(e.target.value)} className="h-8 text-sm" />
                </div>
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-xs text-muted-foreground">HTML content (rendered fullscreen in the ad frame)</Label>
                  <span className="text-[11px] text-muted-foreground bg-muted px-2 py-0.5 rounded font-mono shrink-0">800 × 1000 px · 4:5</span>
                </div>
                <textarea
                  value={htmlContent}
                  onChange={e => setHtmlContent(e.target.value)}
                  rows={6}
                  className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm font-mono resize-y"
                  placeholder={'<div style="background:#000;color:#fff;height:100%;display:flex;align-items:center;justify-content:center;font-size:2rem;">\n  Your ad here\n</div>'}
                />
              </div>
              {/* Replay config */}
              <div className="flex items-center gap-3 flex-wrap">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={htmlReplayEnabled}
                    onChange={e => setHtmlReplayEnabled(e.target.checked)}
                    className="h-3.5 w-3.5 rounded accent-primary"
                  />
                  <span className="text-xs text-muted-foreground">Replay intro every</span>
                </label>
                <div className="flex items-center gap-1.5">
                  <input
                    type="number" min={5} max={600}
                    value={htmlReplayInterval || ''}
                    disabled={!htmlReplayEnabled}
                    onChange={e => setHtmlReplayInterval(parseInt(e.target.value) || 0)}
                    onBlur={() => setHtmlReplayInterval(v => v < 5 ? 5 : v)}
                    className="w-14 h-7 px-2 text-xs rounded border border-input bg-background text-center disabled:opacity-40"
                  />
                  <span className="text-xs text-muted-foreground">seconds</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={handleSaveHtmlAsset} disabled={!htmlAssetName.trim() || !htmlContent.trim() || savingHtml} className="gap-2">
                  {savingHtml ? 'Saving…' : <><Plus className="h-3 w-3" /> Save HTML Ad</>}
                </Button>
                <Button size="sm" variant="outline" disabled={!htmlContent.trim()} onClick={() => setPreviewHtml(htmlContent)} className="gap-1.5">
                  <Eye className="h-3.5 w-3.5" /> Preview
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Asset grid */}
          {assetsLoading && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {[1,2,3,4].map(i => <div key={i} className="aspect-[4/5] rounded-lg bg-gray-100 dark:bg-gray-800 animate-pulse" />)}
            </div>
          )}
          {!assetsLoading && assets.length === 0 && (
            <div className="flex flex-col items-center py-12 text-muted-foreground gap-2">
              <ImageIcon className="h-8 w-8 opacity-30" />
              <p className="text-sm">No assets yet</p>
            </div>
          )}
          {assets.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {assets.map(a => (
                <div key={a.id} className="group relative rounded-lg overflow-hidden border bg-gray-50 dark:bg-gray-900">
                  <button
                    className="w-full aspect-[4/5] flex items-center justify-center bg-gray-100 dark:bg-gray-800 overflow-hidden relative"
                    onClick={() => setPreviewAsset(a)}
                    title="Preview"
                  >
                    {a.type === 'image' && a.image_url ? (
                      <img src={a.image_url} alt={a.name} className="w-full h-full object-cover" />
                    ) : a.type === 'html' && a.html ? (
                      <HtmlThumb html={a.html} />
                    ) : (
                      <Code2 className="h-8 w-8 text-gray-400" />
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                      <Eye className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    {a.type === 'html' && (
                      <span className="absolute top-1 left-1 bg-indigo-600 text-white text-[9px] px-1.5 py-0.5 rounded font-medium">HTML</span>
                    )}
                  </button>
                  <div className="p-2 flex items-center justify-between gap-1">
                    <span className="text-xs font-medium truncate">{a.name}</span>
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      {a.type === 'html' && (
                        <button
                          onClick={() => openEditHtmlDialog(a)}
                          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                          title="Edit HTML"
                        >
                          <Pencil className="h-3.5 w-3.5 text-gray-500" />
                        </button>
                      )}
                      <button
                        onClick={() => { if (confirm(`Delete "${a.name}"?`)) deleteAsset.mutate(a); }}
                        className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                      >
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── HTML asset edit dialog ────────────────────────────────────────────── */}
      <Dialog open={!!editingHtmlAsset} onOpenChange={() => setEditingHtmlAsset(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Code2 className="h-4 w-4" /> Edit HTML Asset</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Asset name</Label>
              <Input value={editHtmlName} onChange={e => setEditHtmlName(e.target.value)} className="h-8 text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">HTML content</Label>
              <textarea
                value={editHtmlContent}
                onChange={e => setEditHtmlContent(e.target.value)}
                rows={16}
                className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm font-mono resize-y"
              />
            </div>
            {/* Replay config */}
            <div className="flex items-center gap-3 flex-wrap pt-1">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={editReplayEnabled}
                  onChange={e => setEditReplayEnabled(e.target.checked)}
                  className="h-3.5 w-3.5 rounded accent-primary"
                />
                <span className="text-xs text-muted-foreground">Replay intro every</span>
              </label>
              <div className="flex items-center gap-1.5">
                <input
                  type="number" min={5} max={600}
                  value={editReplayInterval || ''}
                  disabled={!editReplayEnabled}
                  onChange={e => setEditReplayInterval(parseInt(e.target.value) || 0)}
                  onBlur={() => setEditReplayInterval(v => v < 5 ? 5 : v)}
                  className="w-14 h-7 px-2 text-xs rounded border border-input bg-background text-center disabled:opacity-40"
                />
                <span className="text-xs text-muted-foreground">seconds</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingHtmlAsset(null)}>Cancel</Button>
            <Button variant="outline" disabled={!editHtmlContent.trim()} onClick={() => setPreviewHtml(editHtmlContent)} className="gap-1.5">
              <Eye className="h-3.5 w-3.5" /> Preview
            </Button>
            <Button
              onClick={handleUpdateHtmlAsset}
              disabled={!editHtmlName.trim() || !editHtmlContent.trim() || savingEditHtml}
            >
              {savingEditHtml ? 'Saving…' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── HTML preview dialog ───────────────────────────────────────────────── */}
      <Dialog open={previewHtml !== null} onOpenChange={() => setPreviewHtml(null)}>
        <DialogContent className="max-w-xs p-4">
          <DialogHeader>
            <DialogTitle className="text-sm flex items-center gap-2">
              <Eye className="h-4 w-4" /> Ad Preview
            </DialogTitle>
          </DialogHeader>
          <div className="aspect-[4/5] w-full rounded-lg overflow-hidden bg-black border">
            {previewHtml && (
              <iframe
                srcDoc={previewHtml}
                sandbox="allow-scripts"
                className="w-full h-full border-0"
                title="Ad preview"
              />
            )}
          </div>
          <p className="text-[11px] text-center text-muted-foreground">Rendered at 4:5 · matches station display</p>
        </DialogContent>
      </Dialog>

      {/* ── Asset preview dialog ───────────────────────────────────────────────── */}
      <Dialog open={!!previewAsset} onOpenChange={() => setPreviewAsset(null)}>
        <DialogContent className="max-w-xs p-4">
          <DialogHeader>
            <DialogTitle className="text-sm">{previewAsset?.name}</DialogTitle>
          </DialogHeader>
          <div className="aspect-[4/5] rounded-lg overflow-hidden bg-black border">
            {previewAsset?.type === 'image' && previewAsset.image_url && (
              <img src={previewAsset.image_url} className="w-full h-full object-cover" alt={previewAsset.name} />
            )}
            {previewAsset?.type === 'html' && previewAsset.html && (
              <iframe
                srcDoc={previewAsset.html}
                className="w-full h-full border-0"
                sandbox="allow-scripts"
                title="Ad preview"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Create / Edit dialog ───────────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Campaign' : 'New Campaign'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">

            {/* Name */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Campaign name</Label>
              <Input
                placeholder="e.g. Summer Sale — All Shops"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              />
            </div>

            {/* Mode toggle */}
            <div className="space-y-3">
              <Label className="text-xs text-muted-foreground">Ad content</Label>
              <div className="flex rounded-lg border overflow-hidden w-fit">
                {(['single', 'playlist'] as const).map(m => (
                  <button
                    key={m}
                    onClick={() => setForm(f => ({ ...f, mode: m }))}
                    className={cn(
                      'px-4 py-1.5 text-sm font-medium transition-colors flex items-center gap-1.5',
                      form.mode === m
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    {m === 'single' ? <><Image className="h-3.5 w-3.5" /> Single</> : <><LayoutList className="h-3.5 w-3.5" /> Playlist</>}
                  </button>
                ))}
              </div>

              {/* Single asset picker */}
              {form.mode === 'single' && (
                assets.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Upload an asset first in the Assets tab.</p>
                ) : (
                  <div className="grid grid-cols-3 gap-2 max-h-52 overflow-y-auto pr-1">
                    {assets.map(a => (
                      <button
                        key={a.id}
                        onClick={() => setForm(f => ({ ...f, asset_id: a.id }))}
                        className={cn(
                          'rounded-md overflow-hidden border-2 transition-colors text-left',
                          form.asset_id === a.id ? 'border-primary' : 'border-transparent hover:border-gray-300 dark:hover:border-gray-600'
                        )}
                      >
                        <div className="aspect-[4/5] bg-gray-900 flex items-center justify-center overflow-hidden">
                          {a.type === 'image' && a.image_url
                            ? <img src={a.image_url} alt={a.name} className="w-full h-full object-cover" />
                            : a.type === 'html' && a.html
                              ? <HtmlThumb html={a.html} />
                              : <Code2 className="h-6 w-6 text-gray-400" />}
                        </div>
                        <p className="text-[10px] px-1 py-0.5 truncate">{a.name}</p>
                      </button>
                    ))}
                  </div>
                )
              )}

              {/* Playlist builder */}
              {form.mode === 'playlist' && (
                <div className="space-y-2">
                  {form.playlist_items.length === 0 && (
                    <p className="text-sm text-muted-foreground">Add at least one slide below.</p>
                  )}
                  {form.playlist_items.map((item, i) => {
                    const a = assets.find(x => x.id === item.asset_id);
                    return (
                      <div key={item._key} className="flex items-center gap-2 p-2 rounded-md border bg-gray-50 dark:bg-gray-800/50">
                        <div className="w-9 h-11 rounded overflow-hidden bg-gray-900 flex-shrink-0 flex items-center justify-center">
                          {a?.type === 'image' && a.image_url
                            ? <img src={a.image_url} className="w-full h-full object-cover" alt="" />
                            : a?.type === 'html' && a.html
                              ? <HtmlThumb html={a.html} />
                              : <Code2 className="h-4 w-4 text-gray-400" />}
                        </div>
                        <span className="flex-1 text-sm truncate min-w-0">{a?.name ?? 'Unknown'}</span>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <input
                            type="number" min={1} max={300}
                            value={item.duration_seconds || ''}
                            onChange={e => updateSlideDuration(i, parseInt(e.target.value) || 0)}
                            onBlur={e => { const v = parseInt(e.target.value); updateSlideDuration(i, v < 1 ? 10 : v); }}
                            className="w-14 h-7 px-2 text-xs rounded border border-input bg-background text-center"
                          />
                          <span className="text-xs text-muted-foreground">s</span>
                        </div>
                        <button onClick={() => moveSlide(i, -1)} disabled={i === 0} className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30">
                          <ChevronUp className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => moveSlide(i, 1)} disabled={i === form.playlist_items.length - 1} className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30">
                          <ChevronDown className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => removeSlide(i)} className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30">
                          <Trash2 className="h-3.5 w-3.5 text-red-500" />
                        </button>
                      </div>
                    );
                  })}
                  {/* Add slide */}
                  <div className="flex gap-2">
                    <select
                      value={addSlideId}
                      onChange={e => setAddSlideId(e.target.value)}
                      className="flex-1 h-8 px-2 rounded-md border border-input bg-background text-sm"
                    >
                      <option value="">Pick asset to add…</option>
                      {assets.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                    <Button size="sm" variant="outline" onClick={addSlide} disabled={!addSlideId} className="gap-1">
                      <Plus className="h-3.5 w-3.5" /> Add
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Display timing */}
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.has_display_timing}
                  onChange={e => setForm(f => ({ ...f, has_display_timing: e.target.checked }))}
                  className="h-4 w-4 rounded"
                />
                <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                Intermittent display (show / hide cycle)
              </label>
              {form.has_display_timing && (
                <div className="pl-6 space-y-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-muted-foreground whitespace-nowrap">Show for</Label>
                      <Input
                        type="number" min={5} max={3600}
                        value={form.display_duration_seconds || ''}
                        onChange={e => setForm(f => ({ ...f, display_duration_seconds: parseInt(e.target.value) || 0 }))}
                        onBlur={() => setForm(f => ({ ...f, display_duration_seconds: f.display_duration_seconds < 5 ? 5 : f.display_duration_seconds }))}
                        className="h-8 w-20 text-sm text-center"
                      />
                      <span className="text-xs text-muted-foreground">s</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-muted-foreground whitespace-nowrap">Every</Label>
                      <Input
                        type="number" min={10} max={86400}
                        value={form.display_interval_seconds || ''}
                        onChange={e => setForm(f => ({ ...f, display_interval_seconds: parseInt(e.target.value) || 0 }))}
                        onBlur={() => setForm(f => ({ ...f, display_interval_seconds: f.display_interval_seconds < 10 ? 10 : f.display_interval_seconds }))}
                        className="h-8 w-24 text-sm text-center"
                      />
                      <span className="text-xs text-muted-foreground">s</span>
                    </div>
                  </div>
                  {form.display_interval_seconds > form.display_duration_seconds ? (
                    <p className="text-xs text-muted-foreground">
                      Ad shows for {form.display_duration_seconds}s, hides for {form.display_interval_seconds - form.display_duration_seconds}s, then repeats.
                    </p>
                  ) : (
                    <p className="text-xs text-red-500">"Every" must be greater than "Show for".</p>
                  )}
                </div>
              )}
            </div>

            {/* Targeting */}
            <div className="space-y-3">
              <Label className="text-xs text-muted-foreground">Targeting <span className="text-[10px] opacity-60">(leave all empty = all stations)</span></Label>
              <p className="text-[11px] text-muted-foreground bg-muted/50 rounded-md px-2.5 py-1.5 leading-relaxed">
                Within a row, selections are <strong>OR</strong> (any match).
                Across rows, selections are <strong>AND</strong> (must match all).
                <br />
                e.g. selecting a shop <em>and</em> a station means the station must be in that shop.
              </p>

              {/* Live station count */}
              {(() => {
                const matchCount = getTargetedStationIds(form.targeting, stations, builds).size;
                const totalActive = stations.filter(s => s.status !== 'retiring').length;
                const hasAny = !!(form.targeting.locations?.length || form.targeting.families?.length || form.targeting.builds?.length || form.targeting.stations?.length);
                return (
                  <p className={cn('text-xs font-medium flex items-center gap-1.5', matchCount === 0 && hasAny ? 'text-red-500' : 'text-muted-foreground')}>
                    <Monitor className="h-3 w-3" />
                    {matchCount} of {totalActive} station{totalActive !== 1 ? 's' : ''} matched
                    {matchCount === 0 && hasAny && ' — no stations will see this campaign'}
                  </p>
                );
              })()}

              {/* Locations */}
              <div className="space-y-1">
                <span className="text-[11px] font-medium text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" /> Shops</span>
                <div className="flex flex-wrap gap-1">
                  {locations.map(l => {
                    const sel = form.targeting.locations?.includes(l.id);
                    return <button key={l.id} type="button" onClick={() => setForm(f => {
                      const cur = f.targeting.locations ?? [];
                      const next = sel ? cur.filter(x => x !== l.id) : [...cur, l.id];
                      return { ...f, targeting: { ...f.targeting, locations: next } };
                    })} className={cn('px-2 py-0.5 rounded-full text-[11px] border transition-colors', sel ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted/50 text-muted-foreground border-border hover:border-gray-400')}>{l.name}</button>;
                  })}
                </div>
              </div>

              {/* Families */}
              <div className="space-y-1">
                <span className="text-[11px] font-medium text-muted-foreground flex items-center gap-1"><Users className="h-3 w-3" /> Families</span>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(PRODUCT_FAMILIES).map(([code, name]) => {
                    const sel = form.targeting.families?.includes(code);
                    return <button key={code} type="button" onClick={() => setForm(f => {
                      const cur = f.targeting.families ?? [];
                      const next = sel ? cur.filter(x => x !== code) : [...cur, code];
                      return { ...f, targeting: { ...f.targeting, families: next } };
                    })} className={cn('px-2 py-0.5 rounded-full text-[11px] border transition-colors', sel ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted/50 text-muted-foreground border-border hover:border-gray-400')}>{name}</button>;
                  })}
                </div>
              </div>

              {/* Builds (model/SKU) */}
              <div className="space-y-1">
                <span className="text-[11px] font-medium text-muted-foreground flex items-center gap-1"><Package className="h-3 w-3" /> Model / SKU</span>
                <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                  {builds.filter(b => stations.some(s => s.build_id === b.id && s.status !== 'retiring')).map(b => {
                    const sel = form.targeting.builds?.includes(b.id);
                    return <button key={b.id} type="button" onClick={() => setForm(f => {
                      const cur = f.targeting.builds ?? [];
                      const next = sel ? cur.filter(x => x !== b.id) : [...cur, b.id];
                      return { ...f, targeting: { ...f.targeting, builds: next } };
                    })} className={cn('px-2 py-0.5 rounded-full text-[11px] border transition-colors', sel ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted/50 text-muted-foreground border-border hover:border-gray-400')}>{b.sku}</button>;
                  })}
                </div>
              </div>

              {/* Stations */}
              <div className="space-y-1">
                <span className="text-[11px] font-medium text-muted-foreground flex items-center gap-1"><Monitor className="h-3 w-3" /> Stations</span>
                <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                  {stations.filter(s => s.status !== 'retiring').map(s => {
                    const sel = form.targeting.stations?.includes(s.id);
                    return <button key={s.id} type="button" onClick={() => setForm(f => {
                      const cur = f.targeting.stations ?? [];
                      const next = sel ? cur.filter(x => x !== s.id) : [...cur, s.id];
                      return { ...f, targeting: { ...f.targeting, stations: next } };
                    })} className={cn('px-2 py-0.5 rounded-full text-[11px] border transition-colors', sel ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted/50 text-muted-foreground border-border hover:border-gray-400')}>{s.name}</button>;
                  })}
                </div>
              </div>
            </div>

            {/* Conflict warning */}
            {conflicts.length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800/50 dark:bg-amber-900/20 p-3 space-y-1.5">
                <p className="text-xs font-medium text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                  Station overlap with {conflicts.length} other campaign{conflicts.length !== 1 ? 's' : ''}
                </p>
                {conflicts.map(({ campaign: c, count }) => {
                  const rel = c.priority > form.priority
                    ? 'higher priority — will show instead'
                    : c.priority === form.priority
                    ? 'same priority — most recently started wins'
                    : 'lower priority — this campaign will show instead';
                  return (
                    <p key={c.id} className="text-xs text-amber-600 dark:text-amber-500 pl-5">
                      <span className="font-medium">{c.name}</span>
                      {' '}· {count} station{count !== 1 ? 's' : ''} · {rel}
                    </p>
                  );
                })}
              </div>
            )}

            {/* Schedule */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Schedule</Label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.has_start} onChange={e => setForm(f => ({ ...f, has_start: e.target.checked }))} className="h-4 w-4 rounded" />
                  Scheduled start
                </label>
                {form.has_start && (
                  <input type="datetime-local" value={form.starts_at} onChange={e => setForm(f => ({ ...f, starts_at: e.target.value }))} className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm" />
                )}
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.has_end} onChange={e => setForm(f => ({ ...f, has_end: e.target.checked }))} className="h-4 w-4 rounded" />
                  Scheduled end
                </label>
                {form.has_end && (
                  <input type="datetime-local" value={form.ends_at} onChange={e => setForm(f => ({ ...f, ends_at: e.target.value }))} className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm" />
                )}
              </div>
            </div>

            {/* Time-of-day window */}
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.has_time_window}
                  onChange={e => setForm(f => ({ ...f, has_time_window: e.target.checked }))}
                  className="h-4 w-4 rounded"
                />
                <Sun className="h-3.5 w-3.5 text-muted-foreground" />
                Time-of-day window (Malta time)
              </label>
              {form.has_time_window && (
                <div className="pl-6 flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-muted-foreground whitespace-nowrap">From</Label>
                    <input
                      type="time"
                      value={form.show_from}
                      onChange={e => setForm(f => ({ ...f, show_from: e.target.value }))}
                      className="h-8 px-2 rounded-md border border-input bg-background text-sm"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-muted-foreground whitespace-nowrap">Until</Label>
                    <input
                      type="time"
                      value={form.show_until}
                      onChange={e => setForm(f => ({ ...f, show_until: e.target.value }))}
                      className="h-8 px-2 rounded-md border border-input bg-background text-sm"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground w-full">Ad only shows on stations between these hours (Malta time).</p>
                </div>
              )}
            </div>

            {/* Priority + Active */}
            <div className="flex gap-4">
              <div className="space-y-1 flex-1">
                <Label className="text-xs text-muted-foreground">Priority (higher wins)</Label>
                <Input type="number" min={0} max={100} value={form.priority} onChange={e => setForm(f => ({ ...f, priority: parseInt(e.target.value) || 0 }))} className="h-9 text-sm" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Active</Label>
                <div className="flex items-center h-9">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} className="h-4 w-4 rounded" />
                    {form.is_active ? 'Live when saved' : 'Paused'}
                  </label>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={() => upsertCampaign.mutate({ id: editingId ?? undefined, form })}
              disabled={!canSave || upsertCampaign.isPending}
            >
              {upsertCampaign.isPending ? 'Saving…' : editingId ? 'Save Changes' : 'Create Campaign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
